import { useNavigate } from 'react-router-dom'
import { HeroCarousel } from '../components/HeroCarousel'
import { Monogram } from '../components/Monogram'
import { PrimaryButton } from '../components/buttons'
import { formatEventDate } from '../lib/format'
import { useSession } from '../session/context'

const fallbackWelcome =
  'Alles wat je vandaag vastlegt komt hier samen. Deel je foto’s en video’s met het bruidspaar.'

/**
 * Ronde 2 en 3 — de menukaart. Eén pagina: op 390px een kolom met de carousel
 * bovenaan, vanaf lg twee kolommen met de carousel links en de actie rechts.
 */
export function LandingPage() {
  const { event, guest } = useSession()
  const navigate = useNavigate()

  // Wie zijn naam al gaf hoeft er niet nog eens om gevraagd te worden.
  // Route-relatief, niet pad-relatief: dit is de index-route van /f/{slug},
  // dus "album" hangt er vanzelf achter.
  const onwards = () => navigate(guest ? 'album' : 'wie-ben-je')

  // Een privé galerij is dicht voor gasten, maar niet voor wie het feest
  // gemaakt heeft: anders is de zip de enige manier om er ooit nog naar te
  // kijken. Hij gaat rechtstreeks naar het album en niet langs "hoe kennen we
  // jou?" — hij komt kijken, niet meedoen, en hoort dus niet als gast in de
  // tellingen te belanden.
  const ownerMayLook = event.isPrivate && event.viewerIsOwner && !guest

  return (
    <main className="flex min-h-full flex-col lg:h-full lg:flex-row lg:overflow-hidden">
      <div className="lg:h-full lg:w-1/2 lg:shrink-0">
        <HeroCarousel items={event.heroMedia} />
      </div>

      <div className="flex flex-1 flex-col items-center px-7 text-center lg:justify-center lg:px-24 lg:py-12">
        {/* Het monogram hangt op mobiel over de carousel heen. `relative`
            hoort erbij: de carousel is zelf gepositioneerd, en zonder dit
            schildert die eroverheen — dan zie je alleen de onderste helft. */}
        <div className="relative z-10 -mt-[34px] lg:mt-0">
          <Monogram coupleNames={event.coupleNames} size={68} />
        </div>

        <div className="order-1 pt-5 lg:contents">
          <h1 className="font-display text-[44px] leading-[1.05] font-normal lg:mt-7 lg:text-[68px]">
            {event.coupleNames}
          </h1>

          {/* Een feest zonder datum toont alleen de locatie; een lege regel
              met alleen een punt ertussen ziet er kaal uit. */}
          {(event.eventDateUtc || event.venue) && (
            <p className="text-ink-faint mt-3 text-xs tracking-[0.24em] uppercase lg:mt-4 lg:text-[13px]">
              {[event.eventDateUtc && formatEventDate(event.eventDateUtc), event.venue]
                .filter(Boolean)
                .join(' · ')}
            </p>
          )}

          {/* Het adres, als de eigenaar er een invulde. Een gast die de
              uitnodiging kwijt is heeft hier meer aan dan aan de naam van de
              zaal alleen, dus het is meteen een link naar de kaart. */}
          {event.address && (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                [event.venue, event.address].filter(Boolean).join(', '),
              )}`}
              target="_blank"
              rel="noreferrer"
              className="text-ink-soft hover:text-accent mt-2 inline-flex items-center gap-1.5 text-[13px] underline decoration-transparent transition-colors hover:decoration-current lg:mt-2.5 lg:text-sm"
            >
              <span aria-hidden="true">◎</span>
              {event.address}
            </a>
          )}

          <div className="bg-line mx-auto my-5 h-px w-10 lg:my-[30px] lg:w-12" />

          <p className="font-display text-ink-muted max-w-[460px] text-[21px] leading-[1.5] text-pretty italic lg:text-[27px]">
            {event.welcomeMessage ?? fallbackWelcome}
          </p>
        </div>

        {/* Alleen op mobiel: duwt de knop naar de onderrand. */}
        <div className="order-2 flex-1 lg:hidden" />

        <div className="order-4 w-full py-6 pb-9 lg:order-3 lg:mt-11 lg:max-w-[420px] lg:py-0">
          {ownerMayLook ? (
            <>
              <PrimaryButton onClick={() => navigate('album')}>Album bekijken</PrimaryButton>
              <p className="text-ink-faint mt-3 text-[13px] leading-[1.45]">
                Deze galerij staat op privé. Jij kan er als eigenaar altijd in; gasten niet, en
                uploaden kan niet meer.
              </p>
            </>
          ) : (
            <PrimaryButton onClick={onwards} disabled={event.isPrivate}>
              {event.isPrivate ? 'Deze galerij is privé' : 'Deel je foto’s'}
            </PrimaryButton>
          )}
        </div>
      </div>
    </main>
  )
}
